/* eslint-disable no-unused-vars */

import { Image, Spin, Table } from 'antd';
import Button from 'components/Button';
import CustomEmptyDescription from 'components/CustomEmptyDescription';
import ModalCommon from 'components/Input/ModalCommon';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import images from 'assets/sheets/images';
import Input from 'components/Input';
import { ErrorMessage } from '../styled';
import PropTypes from 'prop-types';
import { QRServices } from 'services/QRServices';
import { toast } from 'sonner';
import dayjs from 'dayjs';
import CourseScheduleServices from 'services/CourseScheduleServices';

const ModalQRCode = ({ onCancel, isOpen, courseId, onSendEmail }) => {
  const [loading, setLoading] = useState(false);
  const [forms, setForms] = useState([]);
  const [detailSchedule, setDetailSchedule] = useState(null);
  const [errors, setErrors] = useState([]);
  const [qrCodeDetail, setQrCodeDetail] = useState({ schedule: [] });

  const getListQrCode = () => {
    setLoading(true);
    QRServices.getQrCodeDetail(courseId)
      .then((res) => {
        if (res?.isOk) {
          setQrCodeDetail(res?.data);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const getDetailSchedule = () => {
    setLoading(true);
    CourseScheduleServices.getDetail(courseId)
      .then((res) => {
        if (res?.isOk) {
          setDetailSchedule(res?.data);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (courseId) {
      getListQrCode();
      getDetailSchedule();
    }
  }, [courseId]);

  const listQrCode = useMemo(() => {
    const result = [];

    // Only add the new QR code if it exists
    if (qrCodeDetail.qrCodeNew?.qrCode) {
      result.push({
        day: 0,
        alphanumericCharacters:
          qrCodeDetail.qrCodeNew.alphanumericCharacters || '',
        qrCode: `${process.env.REACT_APP_API}/${qrCodeDetail.qrCodeNew.qrCode}`,
        startTime:
          qrCodeDetail.schedule?.[0]?.startTime || new Date().toISOString(),
      });
    }

    if (
      Array.isArray(qrCodeDetail.schedule) &&
      detailSchedule &&
      detailSchedule?.retakeCapacity > 0
    ) {
      result.push(
        ...qrCodeDetail.schedule.map((item) => ({
          ...item,
          alphanumericCharacters: item.alphanumericCharacters || '',
          qrCode: item.qrCode
            ? `${process.env.REACT_APP_API}/${item.qrCode}`
            : '',
          startTime: item.startTime || new Date().toISOString(),
        }))
      );
    }

    return result;
  }, [qrCodeDetail, courseId, detailSchedule]);

  const handleAfterOpen = useCallback(() => {
    const modalBody = document.querySelector('.ant-modal-body');
    if (modalBody) {
      modalBody.scrollTop = 0;
    }
  }, []);

  useLayoutEffect(() => {
    if (!isOpen || loading) return;

    requestAnimationFrame(() => {
      const modalBody = document.querySelector('.ant-modal-body');
      if (modalBody) modalBody.scrollTop = 0;
    });
  }, [isOpen, loading]);

  // Update forms when listQrCode changes
  useEffect(() => {
    if (listQrCode.length > 0) {
      setForms(listQrCode);
    }
  }, [listQrCode]);

  const handleChangeData = (day, e) => {
    const value = e.target.value;

    // 1️⃣ Update form (luôn làm đầu tiên)
    setForms((prev) =>
      prev.map((form) =>
        form.day === day
          ? { ...form, alphanumericCharacters: value }
          : form
      )
    );

    // 2️⃣ Xác định message lỗi (nếu có)
    let errorMessage = null;

    if (!value) {
      errorMessage = '設定したテキストを入力してください。';
    } else if (!/^(?=.*[A-Za-z])(?=.*\d)[A-Za-z0-9 _-]{1,12}$/.test(value)) {
      errorMessage = '設定したテキストを正しく入力してください。';
    }

    setErrors((prev) => {
      const filtered = prev.filter(
        (err) => !(err.key === day && err.label === 'text')
      );

      if (!errorMessage) return filtered;

      return [
        ...filtered,
        {
          label: 'text',
          key: day,
          message: errorMessage,
        },
      ];
    });
  };


  const handleDownload = (body) => {
    if (courseId && body.day) {
      const index = detailSchedule?.schedule.findIndex(
        (item) => item.day === body.day
      );

      const name = body.alphanumericCharacters;

      fetch(forms[index + 1].qrCode)
        .then((res) => res.blob())
        .then((blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;

          a.download = name ? `${name}.jpg` : 'qr-code.jpg';

          document.body.appendChild(a);
          a.click();
          a.remove();
          URL.revokeObjectURL(url);
        })
        .catch((err) => {
          return err
        });
    } else {
      if (body.day === 0) {
        const index = forms.findIndex((item) => item.day === 0);
        const name = body.alphanumericCharacters;

        fetch(forms[index].qrCode)
          .then((res) => res.blob())
          .then((blob) => {
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;

            a.download = name ? `${name}.jpg` : 'qr-code.jpg';

            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
          })
          .catch((err) => {
            return (err);
          });
      }
    }
  };

  const handleSave = (day, type) => {

    if (errors.length > 0) return;

    if (day === undefined) return;

    // Clear any previous errors for this key
    setErrors((prev) => prev.filter((err) => err.key !== day));

    // Find the form with the matching day
    const form = forms.find((f) => f.day === day);
    const value = form?.alphanumericCharacters?.trim() || '';

    if (!value) {
      setErrors((prev) => [
        ...prev,
        {
          label: 'text',
          key: day,
          message: '設定したテキストを入力してください。',
        },
      ]);
      return;
    }

    if (!/^(?=.*[A-Za-z])(?=.*\d)[A-Za-z0-9 _-]{1,12}$/.test(value)) {
      setErrors((prev) => {
        const filtered = prev.filter(
          (e) => !(e.label === 'text' && e.key === day)
        );

        return [
          ...filtered,
          {
            label: 'text',
            key: day,
            message: '設定したテキストを正しく入力してください。',
          },
        ];
      });
      return;
    }

    updateQrCode({
      day,
      type,
      alphanumericCharacters: value,
    });
  };

  const updateQrCode = (body) => {
    setLoading(true);
    QRServices.updateQrCode(courseId, {
      day: body.type !== 'NEW' && body.day,
      type: body.type,
      alphanumericCharacters: body.alphanumericCharacters,
    })
      .then(() => {
        getListQrCode();
      })
      .catch((err) =>
        toast.error(
          err?.message || '処理に失敗しました。もう一度お試しください。'
        )
      )
      .finally(() => setLoading(false));
  };

  const columns = [
    {
      title: '開催日',
      dataIndex: 'day',
      key: 'day',
      width: 97,
      render: (_, record) => {
        return (
          <div className="text-left">
            {record.day === 0 ? '新規' : `${record.day} 日目`}
          </div>
        );
      },
    },
    {
      title: '開催日時',
      dataIndex: 'date',
      key: 'date',
      align: 'left',
      width: 209,
      render: (_, record) => (
        <div className="text-left">
          {dayjs(record.startTime).format('YYYY/MM/DD')}
        </div>
      ),
    },
    {
      title: '設定したテキスト（英数字）',
      dataIndex: 'text',
      key: 'text',
      width: 232,
      render: (text, record) => (
        <div className="mt-[20px] flex items-start gap-2">
          <div>
            <Input
              title="text"
              width={263}
              height={36}
              placeholder=""
              rules={[
                {
                  required: true,
                  message: '設定したテキストを入力してください。',
                },
                {
                  validator: (_, value) => {
                    if (!value)
                      return Promise.reject(
                        '設定したテキストを入力してください。'
                      );
                    if (
                      !/^(?=.*[A-Za-z])(?=.*\d)[A-Za-z0-9 _-]{1,12}$/.test(value)
                    ) {
                      return Promise.reject(
                        '設定したテキストを正しく入力してください。'
                      );
                    }
                    return Promise.resolve();
                  },
                  validateTrigger: 'onChange',
                },
              ]}
              onChange={(e) => handleChangeData(record.day, e)}
              errors={errors}
              value={forms?.[record.day]?.alphanumericCharacters ?? ''}
            />
            <ErrorMessage
              className="xl:!pl-0 2xl:!pl-0"
              errors={errors.map((error) => error.message)}
              label="text"
              key={record.day}
            >
              <div className="h-[18px] text-[12px] font-normal">
                {errors?.map(
                  (error) =>
                    error.key === record.day && (
                      <div key={error.key}>{error.message}</div>
                    )
                )}
              </div>
            </ErrorMessage>
          </div>
          <div
            onClick={() =>
              handleSave(record?.day, record?.day === 0 ? 'NEW' : 'RETAKE')
            }
            className="flex h-[36px] w-[36px] 2xl:h-[32.4px] 2xl:w-[32.4px] xl:h-[28.8px] xl:w-[28.8px] items-center justify-center rounded-[8px] border border-[#E6EBF1] bg-white hover:cursor-pointer hover:bg-gray-50"
          >
            <images.save.src />
          </div>
        </div>
      ),
    },
    {
      title: 'QR コード',
      key: 'qrCode',
      width: 277,
      align: 'center',
      render: (_, record) => (
        <div className="flex flex-col items-center">
          <Image src={record.qrCode} width={130} height={155} />
        </div>
      ),
    },
    {
      title: 'アクション',
      key: 'action',
      width: 182,
      render: (_, record) => (
        <div className="flex gap-2">
          <div
            onClick={() => handleDownload(record)}
            className="flex h-[36px] w-[36px] items-center justify-center rounded-[8px] border border-[#E6EBF1] bg-white hover:cursor-pointer hover:bg-gray-50"
          >
            <images.download.src />
          </div>
        </div>
      ),
    },
  ];
  return (
    <ModalCommon
      title="QRコード管理"
      open={isOpen}
      onAfterOpen={handleAfterOpen}
      width={1100}
      onCancel={() => {
        onCancel();
        setErrors([]);
      }}
      footer={
        <div className="flex h-max justify-end gap-4 border-t border-[#e9e9e9] pt-4">
          <Button
            btnType="back"
            onClick={() => {
              onCancel();
              setErrors([]);
            }}
            height={40}
            width={120}
          >
            戻る
          </Button>
        </div>
      }
    >
      <Spin spinning={loading}>
        <Table
          columns={columns}
          dataSource={listQrCode || []}
          rowKey={(record) => `qr-row-${record.day}`}
          pagination={false}
          locale={{
            emptyText: <CustomEmptyDescription />,
          }}
        />
      </Spin>
    </ModalCommon>
  );
};

ModalQRCode.propTypes = {
  isOpen: PropTypes.bool,
  onCancel: PropTypes.func,
  onSendEmail: PropTypes.func,
};

export default ModalQRCode;
